import { useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import { fullName } from '../List'
import useSaveSection from '../useSaveSection'

export default function PeopleSection({ request, onClose }) {
  const { t } = useTranslation()
  const { save, saving } = useSaveSection(request)
  const parents = request.secondaryParents ?? []
  const [selected, setSelected] = useState(() => parents.map(parent => String(parent.id)))

  const toggle = id => setSelected(prev => (prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]))
  const changed = selected.length !== parents.length

  const onSave = async () => {
    const secondaryParentIds = selected.length ? selected.map(Number) : null
    if (await save({ secondaryParentIds }, 'People updated')) onClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={t('People affected')} small close={onClose} borderBottom />

      <Stack spacing={2.5} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('Requesting parent')}
          </Typography>
          <Typography variant="body2">{fullName(request.primaryParent)}</Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2">{t('Other parents')}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('Uncheck a parent to leave them out of the address change.')}
          </Typography>
          {parents.length ? (
            <Stack spacing={1}>
              {parents.map(parent => {
                const id = String(parent.id)
                return (
                  <Paper key={id} variant="outlined" sx={{ borderColor: selected.includes(id) ? 'primary.main' : 'divider' }}>
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', px: 1, py: 0.25 }}
                      control={<Checkbox checked={selected.includes(id)} onChange={() => toggle(id)} />}
                      label={<Typography variant="body2">{fullName(parent)}</Typography>}
                    />
                  </Paper>
                )
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('No other parents in this request')}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('Students')}
          </Typography>
          <Typography variant="body2">{(request.students ?? []).map(fullName).join(', ') || '--'}</Typography>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="contained" disabled={saving || !changed} onClick={onSave}>
          {t('Save')}
        </Button>
      </Stack>
    </Box>
  )
}
